import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  UserCog, Mail, Bell, Globe, Save, User, Lock, 
  Shield, HelpCircle
} from "lucide-react";
import SettingsModalRouter from "../components/SettingsModalRouter";

const settingOptions = [
  {
    icon: <User size={16} />, key: "account",
    title: "Your account",
    description: "Download your data or deactivate your account",
  },
  {
    icon: <Lock size={16} />, key: "security",
    title: "Security and account access",
    description: "Manage security and connected apps",
  },
  {
    icon: <Shield size={16} />, key: "privacy",
    title: "Privacy and safety",
    description: "Control what you see and share",
  },
  {
    icon: <Bell size={16} />, key: "notifications",
    title: "Notifications",
    description: "Adjust notification preferences",
  },
  {
    icon: <Globe size={16} />, key: "display",
    title: "Accessibility, display and languages",
    description: "Display and language settings",
  },
  {
    icon: <HelpCircle size={16} />, key: "resources",
    title: "Additional resources",
    description: "Find help and more info",
  },
];

const UserSettings = () => {
  const { currentUser } = useAuth();
  const [name, setName] = useState(currentUser?.displayName || "");
  const [email] = useState(currentUser?.email || "");
  const [message, setMessage] = useState("");
  const [activeModal, setActiveModal] = useState(null);

  const [preferences, setPreferences] = useState({
    language: "English",
    notifications: true,
  });

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!currentUser) return;
      const ref = doc(db, "preferences", currentUser.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setPreferences(snap.data());
      }
    };
    fetchPreferences();
  }, [currentUser]);

  const handleSave = async () => {
    try {
      if (currentUser && name !== currentUser.displayName) {
        await currentUser.updateProfile({ displayName: name });
        setMessage("✅ Name updated successfully.");
      }

      const ref = doc(db, "preferences", currentUser.uid);
      await setDoc(ref, preferences, { merge: true });

      if (!message) setMessage("✅ Settings saved.");
    } catch (error) {
      console.error("Error:", error);
      setMessage("❌ Failed to update settings.");
    }
  };

  const handleToggleNotifications = () => {
    setPreferences((prev) => ({ ...prev, notifications: !prev.notifications }));
  };

  const handleLanguageChange = (e) => {
    setPreferences((prev) => ({ ...prev, language: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-5xl mx-auto">
      <h2 className="text-xl font-semibold flex items-center gap-2 mb-6">
        <UserCog size={20} /> User Settings
      </h2>

      {/* Profile Form Section */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <label className="block text-xs font-medium mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm min-h-11"
          />
          <label className="block mt-4 text-xs font-medium mb-1">Email</label>
          <div className="flex items-center gap-1 text-sm text-gray-700 break-words">
            <Mail size={14} /> {email}
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="text-sm font-medium">Notifications</div>
              <p className="text-xs text-gray-500">Enable or disable alerts</p>
            </div>
            <input
              type="checkbox"
              checked={preferences.notifications}
              onChange={handleToggleNotifications}
              className="form-checkbox h-5 w-5"
            />
          </div>

          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-medium">Language</div>
              <p className="text-xs text-gray-500">Display language</p>
            </div>
            <select
              value={preferences.language}
              onChange={handleLanguageChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm min-h-11"
            >
              <option>English</option>
              <option>French</option>
              <option>Spanish</option>
              <option>Zulu</option>
              <option>Xhosa</option>
            </select>
          </div>
        </div>
      </div>

      {/* Settings Grid Section with Modals */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {settingOptions.map((item, index) => (
          <button
            key={index}
            onClick={() => setActiveModal(item.key)}
            className="text-left w-full bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition"
          >
            <div className="flex items-start gap-3">
              <div className="text-blue-600 mt-1">{item.icon}</div>
              <div>
                <div className="text-sm font-semibold text-blue-700 hover:underline">{item.title}</div>
                <div className="text-xs text-gray-500">{item.description}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Save Button */}
      <div className="flex justify-center">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-11"
        >
          <Save size={16} /> Save
        </button>
      </div>

      {message && (
        <p className={`mt-3 text-xs text-center ${message.startsWith("✅") ? "text-green-600" : "text-red-600"}`}>
          {message}
        </p>
      )}

      <SettingsModalRouter type={activeModal} onClose={() => setActiveModal(null)} />
    </div>
  );
};

export default UserSettings;
